import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

const SHOW_PAGE_ATTRIBUTE = 'data-show-page';
const PAGE_ATTRIBUTE = 'data-page';

export default class BaseOverlay extends HTMLElement {
    constructor() {
        super();
        this._toggleListeners = new Map();
        this._delegatedCleanupFunctions = [];
    }

    static get observedAttributes() {
        return ['open'];
    }

    connectedCallback() {
        this._init();
    }

    disconnectedCallback() {
        this.destroy();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'open') {
            if (newValue !== null && oldValue === null) {
                this._handleOpen();
            } else if (newValue === null && oldValue !== null) {
                this._handleClose();
            }
        }
    }

    _init() {
        this.sectionId = this.closest('section')?.id?.replace('shopify-section-', '');
        this.setupToggles();
        this.setupDelegatedShowPage();

        this.setupEventListeners();

        if (this.sectionId && window.Shopify?.designMode) {
            this.bindThemeEditorEvents();
        }
    }

    setupToggles() {
        const toggleAttribute = this.getToggleAttribute();
        if (!toggleAttribute) return;

        this.toggleSelector = `[${toggleAttribute}]`;

        this.setupDelegatedToggle();
    }

    setupDelegatedToggle() {
        const cleanup = this.delegateClickDocument(this.toggleSelector, (match, evt) => {
            evt.preventDefault();
            const wasOpen = this.isOpen;
            this.toggle();
            if (wasOpen) this.swallowNextClick();
        });
        this._delegatedCleanupFunctions.push(cleanup);
    }

    /**
     * Cancel the next click, once, after a dismissal a finger caused.
     *
     * Tapping a dialog's dismiss control on a phone closed the dialog and then
     * activated whatever it had been covering, usually a gallery image that
     * opened its lightbox. Reported on the market switcher's No thanks and on
     * the engraving dialog's close and Apply.
     *
     * One tap is producing two clicks: the first does the dismissal, the
     * second lands on what the overlay was hiding, because by then it is gone.
     * A single mis-targeted click cannot explain it, since the dialog would
     * not have closed at all.
     *
     * Capture phase on document, so it runs before anything underneath sees
     * it. Registering it here cannot eat the click currently being handled:
     * document's capture phase for that one has already passed by the time
     * this runs, which is on documentElement during bubble.
     *
     * Armed only from the two dismissals a finger can cause, this and the
     * scrim. Escape and a programmatic close leave it alone, or the next
     * honest click would be swallowed.
     */
    swallowNextClick() {
        if (this._swallowCleanup) this._swallowCleanup();

        const eat = (evt) => {
            evt.preventDefault();
            evt.stopPropagation();
            this._swallowCleanup?.();
        };

        // Long enough to cover the compatibility click, short enough that a
        // deliberate second tap still lands.
        const timer = setTimeout(() => this._swallowCleanup?.(), 400);

        this._swallowCleanup = () => {
            document.removeEventListener('click', eat, true);
            clearTimeout(timer);
            this._swallowCleanup = null;
        };

        document.addEventListener('click', eat, true);
    }

    setupDelegatedShowPage() {
        const cleanup = this.delegateClick(`[${SHOW_PAGE_ATTRIBUTE}]`, (match, evt) => {
            // Only handle the event if the clicked element is within this overlay
            if (!this.contains(match)) {
                return;
            }
            evt.preventDefault();
            this.showPage(match.dataset.showPage);
        });
        this._delegatedCleanupFunctions.push(cleanup);
    }

    setupEventListeners() {
        this.addEventListener('keyup', this.handleKeyUp.bind(this));

        if (this.shouldCloseOnOutsideClick()) {
            // A press that starts inside the content and is released on the
            // overlay still produces a click, dispatched on the common
            // ancestor (this element), which looked like an outside click and
            // closed the dialog mid-drag. Remember where the press began.
            this.addEventListener('pointerdown', this.handlePointerDown.bind(this));
            this.addEventListener('click', this.handleOverlayClick.bind(this));
        }
    }

    handlePointerDown(evt) {
        const content = this.getContentElement();
        this.pressStartedInContent = !!(content && content.contains(evt.target));
    }

    handleKeyUp(evt) {
        if (evt.key === 'Escape' && this.isOpen) {
            this.close();
        }
    }

    handleOverlayClick(evt) {
        const content = this.getContentElement();
        if (this.pressStartedInContent) {
            this.pressStartedInContent = false;
            return;
        }
        if (content && !content.contains(evt.target) && evt.target === this) {
            this.close();
            this.swallowNextClick();
        }
    }

    get isOpen() {
        return this.getAttribute('open') !== null;
    }

    toggle() {
        this.isOpen ? this.close() : this.open();
    }

    open() {
        if (this.isOpen) return;
        this.setAttribute('open', '');
    }

    close() {
        if (!this.isOpen) return;
        this.removeAttribute('open');
    }

    _handleOpen() {
        const scrollTarget = this.getScrollLockTarget();
        if (scrollTarget) {
            disableBodyScroll(scrollTarget);
        }

        this.onOpen();
        this.dispatchEvent(new CustomEvent('overlay:open', { bubbles: true }));
    }

    _handleClose() {
        const scrollTarget = this.getScrollLockTarget();
        if (scrollTarget) {
            enableBodyScroll(scrollTarget);
        }

        this.onClose();
        this.dispatchEvent(new CustomEvent('overlay:close', { bubbles: true }));
    }

    destroy() {
        this.close();

        // Clean up direct listeners
        this._toggleListeners.forEach((handler, el) => {
            el.removeEventListener('click', handler);
        });
        this._toggleListeners.clear();

        // Clean up delegated listeners
        this._delegatedCleanupFunctions.forEach((cleanup) => cleanup());
        this._delegatedCleanupFunctions = [];

        // A pending swallow would otherwise outlive the overlay and cancel a
        // click meant for whatever replaced it.
        this._swallowCleanup?.();
    }

    /**
     * Utility method for delegated event handling (scoped to overlay)
     *
     * These used to run off touchend, to skip the 300ms tap delay. No mobile
     * browser has applied that delay since the viewport meta became standard,
     * and layout/theme.liquid carries width=device-width, so the only thing
     * it bought was a ghost click: the handler closed the overlay on touchend
     * and the browser then synthesised a click at the same coordinates a
     * moment later, which landed on whatever the page had put under the
     * finger. Native click cannot do that, because it is the real event, and
     * it resolves a press and release at different points to their common
     * ancestor on its own.
     *
     * @param {string} selector - CSS selector to match against
     * @param {function} callback - Callback function (match, event) => {}
     * @returns {function} Cleanup function to remove the listener
     */
    delegateClick(selector, callback) {
        const handler = (e) => {
            const match = e.target.closest(selector);
            if (match && this.contains(match)) {
                callback(match, e);
            }
        };

        this.addEventListener('click', handler);
        return () => this.removeEventListener('click', handler);
    }

    /**
     * Utility method for document-level delegated event handling
     * @param {string} selector - CSS selector to match against
     * @param {function} callback - Callback function (match, event) => {}
     * @returns {function} Cleanup function to remove the listener
     */
    delegateClickDocument(selector, callback) {
        const handler = (e) => {
            const match = e.target.closest(selector);
            if (match && document.contains(match)) {
                callback(match, e);
            }
        };

        document.documentElement.addEventListener('click', handler);
        return () => document.documentElement.removeEventListener('click', handler);
    }

    // Methods to be overridden by subclasses
    getToggleAttribute() {
        return this.dataset.toggleAttribute;
    }

    getContentElement() {
        return this.querySelector('.overlay__content, .drawer__inner, .modal__content');
    }

    getScrollLockTarget() {
        return this.getContentElement() || this;
    }

    shouldCloseOnOutsideClick() {
        return false;
    }

    useDelegation() {
        return this.hasAttribute('data-use-delegation');
    }

    onOpen() {
        // Override in subclass
    }

    onClose() {
        // Override in subclass
    }

    bindThemeEditorEvents() {
        document.addEventListener('shopify:section:load', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.onThemeEditorLoad(e);
            }
        });

        document.addEventListener('shopify:section:select', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.open();
            }
        });

        document.addEventListener('shopify:block:select', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.onThemeEditorBlockSelect(e);
                this.open();
            }
        });

        document.addEventListener('shopify:section:deselect', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.close();
            }
        });

        document.addEventListener('shopify:block:deselect', (e) => {
            if (e.detail.sectionId === this.sectionId) {
                this.onThemeEditorBlockDeselect(e);
            }
        });
    }

    onThemeEditorLoad(event) {
        // Override in subclass if needed
    }

    onThemeEditorBlockSelect(event) {
        // Override in subclass if needed
    }

    onThemeEditorBlockDeselect(event) {
        // Override in subclass if needed
    }

    showPage(page) {
        console.log('showPage', page);
        const showingPageEl = this.querySelector(`[${PAGE_ATTRIBUTE}="${page}"]`);

        if (!showingPageEl) {
            throw new Error(`Page with ${PAGE_ATTRIBUTE}="${page}" not found`);
        }

        this.querySelectorAll(`[${PAGE_ATTRIBUTE}]`).forEach((pageEl) => {
            pageEl.classList.add('js-hidden');
        });

        showingPageEl.classList.remove('js-hidden');
    }
}

customElements.define('base-overlay', BaseOverlay);
